import { memo, type FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AudienceListSelect from "src/components/reusable/AudienceListSelect/AudienceListSelect";
import { getRecipientCount, type AudienceListOption } from "src/utils/audienceListLabels";
import { isValidEmail } from "src/utils/Email";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFormField from "../../../../shared/CampaignFormField";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import CampaignTextField from "../../../../shared/CampaignTextField";
import { getFieldErrorId, useRequiredFieldError } from "../../../../shared/UseRequiredFieldError";
import SubjectLineField, { type SubjectSuggestionsState } from "../components/SubjectLineField";
import { MAX_REPLY_TO_LENGTH } from "../AiCampaignUtils";
import { MAX_SUBJECT_LENGTH } from "../AiCampaignConstants";
import type { CampaignSenderDetails } from "../AiCampaignTypes";

const KEY = "campaignFlow.aiWizard.senderRecipients";
const ACCOUNT_SETTINGS_ROUTE = "/account/my-account/account-settings";
const POPUP_CLASS = "campaign-flow__popup";

const FIELD_IDS = {
  subjectLine: "ai-sender-subject-line",
  fromName: "ai-sender-from-name",
  fromEmail: "ai-sender-from-email",
  replyTo: "ai-sender-reply-to",
  sendTo: "ai-sender-send-to",
};

interface SenderRecipientsStepProps {
  subjectLine: string;
  onSubjectLineChange: (_value: string) => void;
  subjectSuggestions?: SubjectSuggestionsState;
  senderDetails: CampaignSenderDetails;
  sendTo: string[];
  audienceOptions: AudienceListOption[];
  isListsLoading: boolean;
  onReplyToChange: (_value: string) => void;
  onSendToChange: (_value: string[]) => void;
}

const SenderRecipientsStep: FC<SenderRecipientsStepProps> = ({
  subjectLine,
  onSubjectLineChange,
  subjectSuggestions,
  senderDetails,
  sendTo,
  audienceOptions,
  isListsLoading,
  onReplyToChange,
  onSendToChange,
}) => {
  const { t, i18n } = useTranslation();
  const sendToField = useRequiredFieldError(sendTo, true);

  const recipientCount = getRecipientCount(audienceOptions, sendTo);
  const getReplyToFormatError = (value: string) =>
    isValidEmail(value) ? null : t(`${KEY}.replyToInvalid`);

  return (
    <CampaignStepCard title={t(`${KEY}.title`)} subtitle={t(`${KEY}.subtitle`)}>
      <section className="campaign-flow__section">
        <h3 className="campaign-flow__section-title">{t(`${KEY}.email`)}</h3>
        {subjectSuggestions ? (
          <SubjectLineField
            id={FIELD_IDS.subjectLine}
            value={subjectLine}
            onChange={onSubjectLineChange}
            suggestions={subjectSuggestions}
          />
        ) : (
          <CampaignTextField
            id={FIELD_IDS.subjectLine}
            label={t("campaignFlow.aiWizard.details.subjectLine")}
            value={subjectLine}
            onChange={onSubjectLineChange}
            placeholder={t(`${KEY}.subjectPlaceholderPlain`)}
            maxLength={MAX_SUBJECT_LENGTH}
            required
          />
        )}
      </section>

      <section className="campaign-flow__section">
        <h3 className="campaign-flow__section-title">{t(`${KEY}.sender`)}</h3>
        <div className="campaign-flow__field-row">
          <CampaignTextField
            id={FIELD_IDS.fromName}
            label={t("campaignFlow.aiWizard.details.fromName")}
            value={senderDetails.fromName}
            required
            disabled
          />
          <CampaignTextField
            id={FIELD_IDS.fromEmail}
            label={t("campaignFlow.aiWizard.details.fromEmail")}
            value={senderDetails.fromEmail}
            required
            disabled
          />
        </div>
        <p className="campaign-flow-field__helper">
          <Trans
            i18nKey={`${KEY}.lockedHint`}
            components={{ settings: <Link to={ACCOUNT_SETTINGS_ROUTE} /> }}
          />
        </p>
        <CampaignTextField
          id={FIELD_IDS.replyTo}
          label={t(`${KEY}.replyTo`)}
          hint={t("campaignFlow.createCampaignNew.optionalHint")}
          value={senderDetails.replyTo}
          onChange={onReplyToChange}
          type="email"
          maxLength={MAX_REPLY_TO_LENGTH}
          placeholder={t(`${KEY}.replyToPlaceholder`)}
          helperText={t(`${KEY}.replyToHelper`)}
          getFormatError={getReplyToFormatError}
        />
      </section>

      <section className="campaign-flow__section">
        <h3 className="campaign-flow__section-title">{t(`${KEY}.recipients`)}</h3>
        <CampaignFormField
          label={t("campaignFlow.createCampaignNew.sendTo")}
          htmlFor={FIELD_IDS.sendTo}
          required
          hasError={sendToField.hasError}
        >
          <AudienceListSelect
            id={FIELD_IDS.sendTo}
            value={sendTo}
            options={audienceOptions}
            onChange={onSendToChange}
            onBlur={sendToField.handleBlur}
            aria-invalid={sendToField.hasError}
            aria-describedby={
              sendToField.hasError ? getFieldErrorId(FIELD_IDS.sendTo) : undefined
            }
            loading={isListsLoading}
            placeholder={t("campaignFlow.createCampaignNew.selectAudience")}
            popupClassName={POPUP_CLASS}
            showSelectedIcon
          />
        </CampaignFormField>
        {recipientCount !== null && (
          <p className="campaign-flow-field__helper">
            {t(`${KEY}.recipientsSummary`, {
              count: formatNumber(recipientCount, {}, i18n.language),
            })}
          </p>
        )}
      </section>
    </CampaignStepCard>
  );
};

export default memo(SenderRecipientsStep);
